import { ValidationErrorMessages } from '@constants/forms';
import { MAX_KWH_INPUT } from '@constants/forms';
import {
  hasElectricityPrepayment,
  hasElectricityService,
  hasGasService,
} from '@features/Energy/utils';
import { EnergyMeterType, PredefinedConsumption } from '@generated/gql/graphql';
import { EnergyDetailsFormValues } from '@src/types/energy';

import { FormErrors } from '../types';

const isPositive = (value: string) => {
  const numberValue = parseFloat(value);

  if (isNaN(numberValue)) {
    return false;
  }

  return numberValue > 0;
};

const isInteger = (value: string) => {
  const numberValue = parseFloat(value);
  return Number.isInteger(numberValue);
};

const validateCustomUsage = (hasService: boolean, value: string) => {
  if (!hasService) {
    return null;
  }

  if (!value) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (!isPositive(value)) {
    return ValidationErrorMessages.INVALID_AMOUNT;
  }

  if (!isInteger(value)) {
    return ValidationErrorMessages.DECIMAL_NOT_ALLOWED;
  }

  if (Number(value) > MAX_KWH_INPUT) {
    return ValidationErrorMessages.KWH_MAX;
  }
  return null;
};

const validateCustomUsageNight = (
  hasService: boolean,
  isEcon7: boolean,
  value: string
) => {
  if (!hasService || !isEcon7) {
    return null;
  }

  if (!value) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (!isPositive(value)) {
    return ValidationErrorMessages.INVALID_AMOUNT;
  }

  return null;
};

const validateForm = (values: EnergyDetailsFormValues) => {
  const {
    electricityUsageDay,
    electricityUsageNight,
    gasUsage,
    predefinedConsumption,
    meterType,
    hasPrepayment,
    paymentOption,
    prepaymentFuelType,
    fuelType,
  } = values;

  const errors: FormErrors = {};

  if (!fuelType) {
    errors.fuelType = ValidationErrorMessages.FIELD_REQUIRED;
  }
  if (!meterType) {
    errors.meterType = ValidationErrorMessages.FIELD_REQUIRED;
  }
  if (
    meterType === EnergyMeterType.Economy_7 &&
    hasElectricityPrepayment({ fuelType, hasPrepayment, prepaymentFuelType })
  ) {
    errors.meterType = ValidationErrorMessages.ECONOMY_7_PREPAYMENT_CONFLICT;
  }
  if (!hasPrepayment) {
    errors.hasPrepayment = ValidationErrorMessages.FIELD_REQUIRED;
  }
  if (!prepaymentFuelType) {
    errors.prepaymentFuelType = ValidationErrorMessages.FIELD_REQUIRED;
  }
  if (!paymentOption) {
    errors.paymentOption = ValidationErrorMessages.FIELD_REQUIRED;
  }
  if (!predefinedConsumption) {
    errors.predefinedConsumption = ValidationErrorMessages.FIELD_REQUIRED;
  }

  if (predefinedConsumption === PredefinedConsumption.Custom) {
    const electricityError = validateCustomUsage(
      hasElectricityService(fuelType),
      electricityUsageDay
    );
    const electricityNightError = validateCustomUsageNight(
      hasElectricityService(fuelType),
      meterType === EnergyMeterType.Economy_7,
      electricityUsageNight
    );
    const gasError = validateCustomUsage(hasGasService(fuelType), gasUsage);

    if (electricityError) {
      errors.electricityUsageDay = electricityError;
    }

    if (electricityNightError) {
      errors.electricityUsageNight = electricityNightError;
    }

    if (gasError) {
      errors.gasUsage = gasError;
    }
  }

  return errors;
};

export { isPositive, validateCustomUsage, validateForm };
